/**
 * HVP Cascade Controller - option loading
 *
 * Batch and single-attribute option loading (client cascade first, AJAX
 * fallback) and the parents-selected gate that keeps deep attributes empty
 * until every attribute above them is chosen.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeOptionsMethods = {
    /**
     * Load options for all remaining attributes
     */
    async loadBatchOptions() {
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ----------------------------------------');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] LOADING BATCH OPTIONS');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ----------------------------------------');

        if (this.isLoading) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Already loading - skipping duplicate request');
            this.log('Already loading, skipping');
            return;
        }

        const start = performance.now();
        this.isLoading = true;
        this.setFormLoading(true);

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Current selections:', JSON.stringify(this.selections));

        try {
            // Check cache first
            const cacheKey = this.cache.buildKey(this.productId, this.selections, 'batch');
            const cached = this.cache.get(cacheKey);

            if (cached) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CACHE HIT - Using cached options');
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Cache key:', cacheKey);
                this.applyBatchOptions(cached);
                this.log('Batch loaded from cache');
                return;
            }

            // USE CLIENT-SIDE CASCADE IF AVAILABLE (instant, no AJAX)
            if (this.clientCascade && this.clientCascade.isAvailable()) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CLIENT-SIDE CASCADE - Computing locally (no AJAX)');

                const batchOptions = this.clientCascade.getBatchOptions(this.selections);

                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Client-side options computed for:', Object.keys(batchOptions));
                for (const [attr, opts] of Object.entries(batchOptions)) {
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   -', attr, ':', Array.isArray(opts) ? opts.length + ' options' : 'invalid');
                }

                // Cache result
                this.cache.set(cacheKey, batchOptions);

                // Apply options
                this.applyBatchOptions(batchOptions);

                const elapsed = Math.round(performance.now() - start);
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CLIENT-SIDE batch complete in', elapsed, 'ms (NO AJAX!)');
                this.log(`Batch loaded in ${elapsed}ms (CLIENT-SIDE, ${Object.keys(batchOptions).length} attributes)`);
                return;
            }

            // FALLBACK: Server-side AJAX (slow - 3+ seconds per call)
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CACHE MISS - Fetching from server (SLOW FALLBACK)');
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Calling API: cascadeBatch');

            // Request from server
            const result = await this.api.cascadeBatch(
                this.productId,
                this.selections,
                this.attributeOrder
            );

            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Server response received');

            if (result && result.options) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Options received for attributes:', Object.keys(result.options));
                for (const [attr, opts] of Object.entries(result.options)) {
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   -', attr, ':', Array.isArray(opts) ? opts.length + ' options' : 'invalid');
                }

                // Cache result
                this.cache.set(cacheKey, result.options);

                // Apply options
                this.applyBatchOptions(result.options);

                const elapsed = Math.round(performance.now() - start);
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Batch load complete in', elapsed, 'ms');
                this.log(`Batch loaded in ${elapsed}ms (${Object.keys(result.options).length} attributes)`);
            } else {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] WARNING: Empty or invalid response from server');
            }

        } catch (error) {
            console.error('[HVP DEBUG] ERROR loading batch options:', error.message);
            console.error('[HVP] Batch load error:', error);
            this.showError('Failed to load options');

        } finally {
            this.isLoading = false;
            this.setFormLoading(false);
        }
    },

    /**
     * Apply batch options to dropdowns
     *
     * Only populates a dropdown when every attribute above it in the cascade order
     * already has a selection. Deep attributes stay empty/disabled until all parents
     * are chosen — this prevents upstream-only filtering from collapsing a deep
     * attribute to one value and visually "pre-selecting" it (single-option styling)
     * before the user has picked the intermediate options.
     */
    applyBatchOptions(options) {
        const updatedAttributes = [];

        for (const [attr, optionsList] of Object.entries(options)) {
            const normalized = this.normalizeAttributeName(attr);

            if (!this.areAllParentsSelected(normalized)) {
                continue;
            }

            this.updateDropdownOptions(normalized, optionsList);
            updatedAttributes.push('attribute_' + normalized);
        }

        // Fire legacy event for turnaround-variations compatibility
        if (updatedAttributes.length > 0) {
            this.fireLegacyUpdatedEvent(updatedAttributes);
        }
    },

    /**
     * Check whether every attribute ordered before the given one has a selection.
     */
    areAllParentsSelected(attribute) {
        const normalized = this.normalizeAttributeName(attribute);
        for (let i = 0; i < this.attributeOrder.length; i++) {
            const attrAtIndex = this.normalizeAttributeName(this.attributeOrder[i]);
            if (attrAtIndex === normalized) {
                return true;
            }
            if (!this.selections[attrAtIndex]) {
                return false;
            }
        }
        return true;
    },

    /**
     * Load options for single attribute
     */
    async loadOptions(attribute) {
        const normalized = this.normalizeAttributeName(attribute);

        // Check cache
        const cacheKey = this.cache.buildKey(this.productId, this.selections, normalized);
        const cached = this.cache.get(cacheKey);

        if (cached) {
            this.updateDropdownOptions(normalized, cached);
            return cached;
        }

        try {
            let options = null;

            // USE CLIENT-SIDE CASCADE IF AVAILABLE (instant, no AJAX)
            if (this.clientCascade && this.clientCascade.isAvailable()) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CLIENT-SIDE loadOptions for', normalized);
                options = this.clientCascade.getOptions(this.selections, normalized);
            } else {
                // FALLBACK: Server-side AJAX (slow)
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SERVER-SIDE loadOptions for', normalized, '(slow fallback)');
                const result = await this.api.cascade(
                    this.productId,
                    this.selections,
                    normalized
                );
                options = result?.options;
            }

            if (options && options.length > 0) {
                this.cache.set(cacheKey, options);
                this.updateDropdownOptions(normalized, options);

                // Fire legacy event for turnaround-variations compatibility
                this.fireLegacyUpdatedEvent(['attribute_' + normalized]);

                return options;
            }

        } catch (error) {
            console.error('[HVP] Load options error:', error);
            throw error;
        }
    },
};

export default CascadeOptionsMethods;
